import i18n from "i18next";
import { StackShim } from "@fluentui/react-migration-v8-v9";
import Header from "../../Layout/Header";
import {
  Button,
  createTableColumn,
  MessageBar,
  MessageBarBody,
  TableCellLayout,
  TableColumnDefinition,
} from "@fluentui/react-components";
import { DocumentPdf24Regular, InfoRegular } from "@fluentui/react-icons";
import LuebPagedDataGrid from "../../../Controls/LuebPagedDataGrid";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { DayOfWeek } from "@fluentui/react-calendar-compat";
import { useEffect, useState } from "react";
import { IDocumentAccessLog } from "../../../Models/IDocumentAccessLog";
import { fetchWrapper } from "../../../helpers/fetchwrapper";
import { baseUrl } from "../../../helpers/constants";

function DocumentAccessLogs() {
  //Logs holen
  const [documentAccessLogs, setDocumentAccessLogs] = useState<
    IDocumentAccessLog[] | undefined
  >(undefined);

  const getDocumentAccessLogs = (date: string) => {
    if (date != undefined && date != "") {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/documents/GetAllDocumentAccessLogsByDate?date=${date}`,
        )
        .then((result: IDocumentAccessLog[]) => {
          setDocumentAccessLogs(result);
        })
        .catch((error) => {});
    }
  };

  //Für DataGrid
  const columns: TableColumnDefinition<IDocumentAccessLog>[] = [
    createTableColumn<IDocumentAccessLog>({
      columnId: "pdfIconId",
      compare: (a, b) => {
        return 0;
      },
      renderHeaderCell: () => {
        return "";
      },
      renderCell: (item) => {
        return <DocumentPdf24Regular />;
      },
    }),
    createTableColumn<IDocumentAccessLog>({
      columnId: "docName", // Kann nicht mit <LuebPagedDataGrid/> sortiert werden, da man hier noch nicht weiß, ob man ein userDocument oder ein dokument hat
      compare: (a, b) => {
        if (a.dokumentGuid && b.dokumentGuid) {
          return a.dokument.docFileName.localeCompare(b.dokument.docFileName);
        } else if (a.userDokumentGuid && b.userDokumentGuid) {
          return a.userDocument.description.localeCompare(
            b.userDocument.description,
          );
        } else return 0;
      },
      renderHeaderCell: () => {
        return "Dokument";
      },
      renderCell: (item) => {
        return (
          <TableCellLayout truncate>
            {item.userDokumentGuid
              ? item.userDocument.description
              : item.dokument.docFileName}
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IDocumentAccessLog>({
      columnId: "date",
      compare: (a, b) => {
        if (a.date && b.date) {
          return a.date.localeCompare(b.date);
        } else return 0;
      },
      renderHeaderCell: () => {
        return "Datum / Uhrzeit";
      },
      renderCell: (item) => {
        return (
          <TableCellLayout truncate>
            {new Date(item.date).toLocaleString().replace(",", " / ")}
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IDocumentAccessLog>({
      columnId: "appUser.fullName",
      compare: (a, b) => {
        if (a.appUser && b.appUser) {
          return a.appUser.fullName.localeCompare(b.appUser.fullName);
        } else return 0;
      },
      renderHeaderCell: () => {
        return "Benutzer";
      },
      renderCell: (item: IDocumentAccessLog) => {
        if (item.appUser != undefined) {
          return (
            <TableCellLayout truncate>{item.appUser.fullName}</TableCellLayout>
          );
        } else {
          return (
            <TableCellLayout truncate>Kein Benutzer gefunden</TableCellLayout>
          );
        }
      },
    }),
    createTableColumn<IDocumentAccessLog>({
      columnId: "item.userDocument",
      compare: (a, b) => {
        return 0;
      },
      renderHeaderCell: () => {
        return "";
      },
      renderCell: (item) => {
        return (
          <TableCellLayout truncate>
            {item.userDocument ? "Benutzer Dokument" : ""}
          </TableCellLayout>
        );
      },
    }),
  ];

  //Für DatePicker
  const [selectedDate, setSelectedDate] = useState<Date | null | undefined>();
  const onSelectDate = (date: Date | null | undefined) => {
    if (date && date <= new Date()) {
      getDocumentAccessLogs(
        date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate(),
      );
      setSelectedDate(date);
    }
  };

  const dateFormatting = (date?: Date): string => {
    return !date
      ? ""
      : date.getDate() + "." + (date.getMonth() + 1) + "." + date.getFullYear();
  };

  useEffect(() => {
    const today = new Date();
    getDocumentAccessLogs(
      today.getFullYear() + "-" + today.getMonth() + "-" + today.getDate(),
    );
    setSelectedDate(today);
  }, []);

  return (
    <>
      <Header
        HTMLElement={
          <StackShim horizontal verticalAlign="center">
            <Button
              icon={<InfoRegular fontSize={24} color="#ff6600" />}
              size="large"
              appearance="subtle"
            >
              {i18n.t<string>("documentAccessLogs").toLocaleUpperCase()}
            </Button>
          </StackShim>
        }
      />

      <div className="lueb-InnerContentBlockPadding10px">
        <DatePicker
          value={selectedDate}
          formatDate={dateFormatting}
          placeholder="Datum auswählen"
          firstDayOfWeek={DayOfWeek.Monday}
          maxDate={new Date()}
          onSelectDate={onSelectDate}
        />
      </div>

      <div className="lueb-ContentBlock">
        <LuebPagedDataGrid
          columns={columns}
          items={documentAccessLogs ?? []}
          resizableColumns={true}
          sortable
          columnSizingOptions={{
            docName: { defaultWidth: 400, minWidth: 350 },
            "appUser.fullName": { defaultWidth: 190, minWidth: 190 },
            pdfIconId: { defaultWidth: 60, minWidth: 60 },
            "item.userDocument": { defaultWidth: 180, minWidth: 180 },
          }}
        />

        {documentAccessLogs == undefined && (
          <MessageBar intent="warning" style={{ marginTop: "5px" }}>
            <MessageBarBody>
              Bitte wählen Sie ein Datum aus um die Suche zu starten
            </MessageBarBody>
          </MessageBar>
        )}

        {documentAccessLogs && documentAccessLogs.length == 0 && (
          <MessageBar intent="info" style={{ marginTop: "5px" }}>
            <MessageBarBody>Es wurden keine Logs gefunden</MessageBarBody>
          </MessageBar>
        )}
      </div>
    </>
  );
}
export default DocumentAccessLogs;
